import React from "react";
import { clsx } from "clsx";

import { CarouselNav } from "~components/shared/carousel";
import { type MediaConfig, Media, MediaFormat } from "~components/shared/media";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import {
    type CuratedItem,
    type VariationComponent,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import { getItemsGroupedByMaxItems } from "../carousel-3-up/LineupContentCarousel3Up";

import integrityCheck from "./LineupContentCarousel3UpVideo.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentCarousel3UpVideo.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

export const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.videoImage];

const MEDIA_CONFIG: MediaConfig = {
    className: styles.media,
    desktopMaxWidth: 265,
    mobileMaxWidth: 224,
    showCaption: false,
    aspectRatio: 16 / 9,
};

const MAX_ITEMS = 3;

const Card = (props: { item: CuratedItem; storyIndex: number }) => {
    const { item, storyIndex } = props;
    const { url, labelDropdown } = item;

    return (
        <div key={url} className={styles.item}>
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <StoryBlock
                className={styles.content}
                story={item}
                storyIndex={storyIndex}
                labelDropdown={labelDropdown}
                isExtraSmallHeadline
                isSecondaryStory
                withDek={false}
            />
        </div>
    );
};

export default function LineupContentCarousel3UpVideo(
    props: VariationComponent
) {
    const { items = [] } = props;

    const integrity = integrityCheck({ ...props });
    if (integrity.length) return integrityError(integrity);

    if (!items?.length) return null;

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContentCarousel3UpVideo}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={clsx([styles.mobile, styles.slide])}>
                {items.map((item, index) => (
                    <Card key={item.url} storyIndex={index + 1} item={item} />
                ))}
            </div>
            <div className={styles.notMobile}>
                <CarouselNav animatedTransition trackingName="carousel3UpVideo">
                    {getItemsGroupedByMaxItems(items, MAX_ITEMS).map(
                        (group, groupIndex) => (
                            <div key={groupIndex} className={styles.slide}>
                                {group.map((item, index) => (
                                    <Card
                                        key={item.url}
                                        storyIndex={
                                            groupIndex * MAX_ITEMS + index + 1
                                        }
                                        item={item}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </CarouselNav>
            </div>
        </VariantBaseWithHeader>
    );
}
